// (c) chaehongee. Confidential and proprietary.

import type { ReactElement } from 'react'
import styles from '../../Canvas.module.css'
import type { Corner, Rect } from '../../types'

type CornerHandle = 'nw' | 'ne' | 'sw' | 'se'

const EDGE_THICKNESS = 8

interface TransformOverlayProps {
    rect: Rect
    selected: boolean
    contentScale: number
    zIndex?: number
    showOutline?: boolean
    showHandles?: boolean
    onSelect?: (e: React.PointerEvent) => void
    onResizeStart?: (corner: Corner, e: React.PointerEvent) => void
    onMoveStart?: (e: React.PointerEvent) => void
    onContextMenu?: (e: React.MouseEvent) => void
    onPointerEnter?: (e: React.PointerEvent) => void
    onPointerLeave?: (e: React.PointerEvent) => void
}

export type TransformHandlePlacement = 'centered' | 'inside'
export type TransformOutlineMode = 'cssOutline' | 'insetBoxShadow'

export interface TransformOverlayBaseProps {
    rect: Rect
    selected: boolean
    contentScale: number
    zIndex?: number
    /**
     * If false, the root overlay does not capture pointer events (useful when the
     * underlying image should handle press-drag for move).
     */
    enableMoveHitArea?: boolean
    onPointerDown?: (e: React.PointerEvent) => void
    onContextMenu?: (e: React.MouseEvent) => void
    onPointerEnter?: (e: React.PointerEvent) => void
    onPointerLeave?: (e: React.PointerEvent) => void
    onResizeStart?: (corner: Corner, e: React.PointerEvent) => void
    handlePlacement?: TransformHandlePlacement
    /**
     * For centered handles, nudge the handle towards the inside of the rect by this many CSS px.
     * This value is applied before the scale() so it remains visually constant across zoom.
     */
    handleInsetPx?: number
    showOutline?: boolean
    outlineMode?: TransformOutlineMode
    showHandles?: boolean
}

export function TransformOverlayBase(props: TransformOverlayBaseProps): ReactElement {
    const {
        rect,
        selected,
        contentScale,
        zIndex,
        enableMoveHitArea = true,
        onPointerDown,
        onContextMenu,
        onPointerEnter,
        onPointerLeave,
        onResizeStart,
        handlePlacement = 'centered',
        handleInsetPx = 3,
        showOutline = true,
        outlineMode = 'cssOutline',
        showHandles = true,
    } = props

    const scale = 1 / (contentScale || 1)
    const edgeThickness = EDGE_THICKNESS * scale

    const tCentered = {
        nw: 'translate(-50%, -50%)',
        ne: 'translate(50%, -50%)',
        sw: 'translate(-50%, 50%)',
        se: 'translate(50%, 50%)',
    } as const

    const handle = (corner: CornerHandle, className: string) => {
        const isInside = handlePlacement === 'inside'
        const insetByCorner: Record<CornerHandle, { dx: number; dy: number }> = {
            nw: { dx: handleInsetPx, dy: handleInsetPx },
            ne: { dx: -handleInsetPx, dy: handleInsetPx },
            sw: { dx: handleInsetPx, dy: -handleInsetPx },
            se: { dx: -handleInsetPx, dy: -handleInsetPx },
        }
        const inset = insetByCorner[corner]
        const centeredTransform = `${tCentered[corner]} translate(${inset.dx}px, ${inset.dy}px) scale(${scale})`
        const transform = isInside ? `scale(${scale})` : centeredTransform
        const transformOriginByCorner: Record<CornerHandle, string> = {
            nw: 'top left',
            ne: 'top right',
            sw: 'bottom left',
            se: 'bottom right',
        }
        const transformOrigin = isInside ? transformOriginByCorner[corner] : 'center'

        return (
            <div
                className={styles.resizeHandle + ' ' + className}
                style={{
                    transform,
                    transformOrigin,
                    pointerEvents: 'auto',
                }}
                onPointerDown={(e) => {
                    e.stopPropagation()
                    onResizeStart?.(corner, e)
                }}
            />
        )
    }

    const rootPointerEvents = enableMoveHitArea ? 'auto' : 'none'
    const outlineClass =
        selected && showOutline && outlineMode === 'cssOutline'
            ? ' ' + styles.worldItemSelected
            : ''

    return (
        <div
            className={styles.worldItem + ' ' + outlineClass}
            data-world-item="true"
            style={{
                left: rect.x,
                top: rect.y,
                width: rect.w,
                height: rect.h,
                zIndex,
                pointerEvents: rootPointerEvents as any,
                ...(selected && showOutline && outlineMode === 'insetBoxShadow'
                    ? { boxShadow: 'inset 0 0 0 calc(2px / var(--content-scale, 1)) var(--color-accent)' }
                    : null),
            }}
            onContextMenu={(e) => onContextMenu?.(e)}
            onPointerEnter={onPointerEnter}
            onPointerLeave={onPointerLeave}
            onPointerDown={(e) => {
                if (!enableMoveHitArea) return
                e.stopPropagation()
                if (e.button === 0) {
                    onPointerDown?.(e)
                }
            }}
        >
            {selected && showHandles && (
                <>
                    {/* Full-edge hit areas for resizing from anywhere on edges */}
                    <div
                        className={styles.edgeN}
                        style={{ height: edgeThickness, pointerEvents: 'auto' }}
                        onPointerDown={(e) => {
                            e.stopPropagation()
                            onResizeStart?.('n', e)
                        }}
                    />
                    <div
                        className={styles.edgeS}
                        style={{ height: edgeThickness, pointerEvents: 'auto' }}
                        onPointerDown={(e) => {
                            e.stopPropagation()
                            onResizeStart?.('s', e)
                        }}
                    />
                    <div
                        className={styles.edgeE}
                        style={{ width: edgeThickness, pointerEvents: 'auto' }}
                        onPointerDown={(e) => {
                            e.stopPropagation()
                            onResizeStart?.('e', e)
                        }}
                    />
                    <div
                        className={styles.edgeW}
                        style={{ width: edgeThickness, pointerEvents: 'auto' }}
                        onPointerDown={(e) => {
                            e.stopPropagation()
                            onResizeStart?.('w', e)
                        }}
                    />
                    {handle('nw', styles.handleNW)}
                    {handle('ne', styles.handleNE)}
                    {handle('sw', styles.handleSW)}
                    {handle('se', styles.handleSE)}
                </>
            )}
        </div>
    )
}

export function TransformOverlay(props: TransformOverlayProps): ReactElement {
    const { rect, selected, contentScale, zIndex, showOutline = true, showHandles = true, onSelect, onResizeStart, onMoveStart, onContextMenu, onPointerEnter, onPointerLeave } = props

    return (
        <TransformOverlayBase
            rect={rect}
            selected={selected}
            contentScale={contentScale}
            zIndex={zIndex}
            enableMoveHitArea={true}
            handlePlacement="centered"
            handleInsetPx={2}
            showOutline={showOutline}
            outlineMode="cssOutline"
            showHandles={showHandles}
            onContextMenu={onContextMenu}
            onPointerEnter={onPointerEnter}
            onPointerLeave={onPointerLeave}
            onPointerDown={(e) => {
                onSelect?.(e)
                onMoveStart?.(e)
            }}
            onResizeStart={onResizeStart}
        />
    )
}

